/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { useRedirectToSubscribe } from "@packages/kids";
import type { Track } from "@packages/sdk";
import { useRequestMe } from "@packages/sdk";
import type {
  DropdownMenuItemProps,
  ModalItemProps,
  ThemeType,
} from "@packages/ui";
import {
  ModalDropdownMenuHeader,
  ModalDropdownMenuItem,
  spacing,
  SubscriptionIcon,
  Text,
} from "@packages/ui";
import type { Dispatch, SetStateAction } from "react";

export type SelectLengthListProps = {
  selectedLength: Partial<Track>;
  setSelectedLength: Dispatch<SetStateAction<Partial<Track>>>;
  options: Partial<Track>[];
};

const menuSubheaderStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.onOverlayMedium,
  height: "auto",
  justifyContent: "flex-start",
  paddingBlock: spacing.m,
  paddingInline: spacing.m,
  textAlign: "left",
  textTransform: "uppercase",
  ":first-of-type": {
    marginTop: "-4px",
  },
});
const menuItemStyle: Interpolation = {
  paddingLeft: spacing.xl,
};
const flexStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  rowGap: spacing.xxs,
};

export const SelectLengthList = ({
  options,
  selectedLength,
  setSelectedLength,
}: SelectLengthListProps) => {
  const t = useTranslations();
  const { query: me } = useRequestMe();
  const redirectToSubscribe = useRedirectToSubscribe();

  const videoGroup = options.filter((trk) => trk.media_type === "video");
  const audioGroup = options.filter((trk) => !videoGroup.includes(trk));

  const optionMap = (option: Partial<Track>) => {
    const hasAccess =
      typeof option.has_access !== "undefined"
        ? option.has_access
        : (option.paid && me?.data?.subscription) || !option.paid;
    const iconProps: DropdownMenuItemProps | ModalItemProps = !hasAccess
      ? { startIcon: <SubscriptionIcon /> }
      : {};
    /** I (nathaniel@hallow.app) made the executive decision to disable this option
     * when the user is a kid, rather than hooking in the switch to parent + subscribe
     * flow. Locked lengths are already exceedingly rare, and going through the flow
     * would immediately stop audio playback (which is why we set newWindow below,
     * but changing profiles will stop playback regardless). Switching to a locked
     * length from the prayer detail screen will still work as expected. */
    const disabled = !!(me.data?.is_kid && !hasAccess);
    return (
      <ModalDropdownMenuItem
        key={`sessionDetails_selectLength_length_${option.id}`}
        selected={option.id === selectedLength?.id}
        onSelect={() => {
          if (!hasAccess) {
            redirectToSubscribe({ newWindow: true });
          } else {
            setSelectedLength(option);
          }
        }}
        dropdownMenuItemCSS={videoGroup.length && menuItemStyle}
        dropdownMenuItemProps={
          { ...iconProps, disabled } as DropdownMenuItemProps
        }
        modalItemCSS={videoGroup.length && menuItemStyle}
        modalItemProps={{ ...iconProps, disabled } as ModalItemProps}
      >
        {option.length}
      </ModalDropdownMenuItem>
    );
  };

  return (
    <div css={flexStyle}>
      <ModalDropdownMenuHeader>{t("media_options")}</ModalDropdownMenuHeader>
      {videoGroup.length ? (
        <>
          <Text type={"title"} size={"s"} css={menuSubheaderStyle}>
            {t("media_type_video")}
          </Text>
          {videoGroup.map(optionMap)}
        </>
      ) : null}
      {audioGroup.length ? (
        <>
          <Text type={"title"} size={"s"} css={menuSubheaderStyle}>
            {t("media_type_audio")}
          </Text>
          {(audioGroup ?? []).map(optionMap)}
        </>
      ) : null}
    </div>
  );
};
